import { Service, OnDestroy, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

@Service()
export class RequestCooldownService implements OnDestroy {
  private readonly deadlines = signal<Record<string, number>>({});
  private readonly now = signal(Date.now());
  private timer?: ReturnType<typeof setTimeout>;

  remaining(operation: string): number {
    return Math.max(0, Math.ceil(((this.deadlines()[operation] ?? 0) - this.now()) / 1000));
  }
  capture(operation: string, error: unknown): boolean {
    const delay = retryAfterMs(error);
    if (delay === null) return false;
    const now = Date.now();
    this.now.set(now);
    this.deadlines.update((values) => ({
      ...values,
      [operation]: Math.max(values[operation] ?? 0, now + delay),
    }));
    this.tick();
    return true;
  }
  private tick() {
    clearTimeout(this.timer);
    const now = Date.now();
    this.now.set(now);
    this.deadlines.update((values) =>
      Object.fromEntries(Object.entries(values).filter(([, time]) => time > now))
    );
    if (Object.keys(this.deadlines()).length) this.timer = setTimeout(() => this.tick(), 1000);
  }
  ngOnDestroy() {
    clearTimeout(this.timer);
  }
}

/** Retry-After is a lower bound, including the HTTP-date form used by proxies. */
export function retryAfterMs(error: unknown): number | null {
  if (!(error instanceof HttpErrorResponse) || error.status !== 429) return null;
  return retryHintMs(error);
}

/**
 * The server could not reach its rate-limit store and asks for a short retry
 * (`503 RATE_LIMIT_UNAVAILABLE` with `Retry-After`). Unlike a 429 this is not
 * throttling, so callers retry it a bounded number of times without a cooldown.
 */
export function limiterUnavailableMs(error: unknown): number | null {
  if (
    !(error instanceof HttpErrorResponse) ||
    error.status !== 503 ||
    error.error?.code !== 'RATE_LIMIT_UNAVAILABLE'
  )
    return null;
  return retryHintMs(error);
}

function retryHintMs(error: HttpErrorResponse): number {
  const raw = error.headers.get('Retry-After');
  const header =
    raw && /^\d+(?:\.\d+)?$/.test(raw.trim())
      ? Number(raw) * 1000
      : raw
        ? Date.parse(raw) - Date.now()
        : NaN;
  const body = Number(error.error?.retryAfter) * 1000;
  const delays = [header, body].filter((n) => Number.isFinite(n) && n > 0);
  return Math.max(1000, ...(delays.length ? delays : [1000]));
}

export function retryDelayWithJitter(delay: number): number {
  return delay + 100 + Math.random() * Math.min(1000, Math.max(100, delay * 0.1));
}
